// src/pages/DownloadTicket.jsx
import React, { useMemo, useRef, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import apiClient from "../api";

const LOGO = "/images/logo-ticket.png";

const DownloadTicket = () => {
    const { state, search } = useLocation();
    const navigate = useNavigate();
    const artboardRef = useRef(null);
    const [logoOk, setLogoOk] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [bookingDetails, setBookingDetails] = useState(state?.bookingDetails || null);

    useEffect(() => {
        const img = new Image();
        img.onload = () => setLogoOk(true);
        img.onerror = () => setLogoOk(false);
        img.src = LOGO;
    }, []);

    const params = useMemo(() => new URLSearchParams(search), [search]);
    const orderId = (
        params.get("order_id") ||
        params.get("orderId") ||
        params.get("bookingNo") ||
        params.get("order-no") ||
        ""
    ).trim();

    useEffect(() => {
        let mounted = true;

        const mapBooking = (booking) => {
            const bus = booking?.bus || {};
            const passengerInfo = booking?.passengerInfo || {};
            const passengers = Array.isArray(booking?.passengers) ? booking.passengers : [];
            const boardingMatch = bus?.boardingPoints?.find(
                (bp) => String(bp?.point || "").trim().toLowerCase() ===
                    String(booking?.from || "").trim().toLowerCase()
            );
            const droppingMatch = bus?.droppingPoints?.find(
                (dp) => String(dp?.point || "").trim().toLowerCase() ===
                    String(booking?.to || "").trim().toLowerCase()
            );
            return {
                bus,
                passenger: {
                    name: passengerInfo?.fullName || "",
                    email: passengerInfo?.email || "",
                    mobile: passengerInfo?.phone || "",
                    nic: passengerInfo?.nic || "",
                },
                passengers,
                selectedSeats: Array.isArray(booking?.selectedSeats)
                    ? booking.selectedSeats
                    : passengers.map((p) => p?.seat).filter(Boolean),
                boardingPoint: {
                    point: booking?.from || boardingMatch?.point || "",
                    time: boardingMatch?.time || booking?.departureTime || "",
                },
                droppingPoint: {
                    point: booking?.to || droppingMatch?.point || "",
                    time: droppingMatch?.time || "",
                },
                priceDetails: {
                    totalPrice: Number(booking?.totalAmount || 0),
                    convenienceFee: Number(booking?.convenienceFee || 0),
                },
                departureTime: booking?.departureTime || "",
                date: booking?.date || "",
                bookingNo: booking?.bookingNo || "",
                bookingNoShort: booking?.bookingNoShort || "",
                bookingId: booking?._id || "",
                paymentStatus: booking?.paymentStatus || "",
                createdAt: booking?.createdAt || "",
            };
        };

        const load = async () => {
            try {
                setIsLoading(true);
                setLoadError("");

                if (orderId) {
                    const { data } = await apiClient.get(`/bookings/ticket/${encodeURIComponent(orderId)}`);
                    if (!data?.booking) throw new Error("Ticket data not found.");
                    const mapped = mapBooking(data.booking);
                    if (!mounted) return;
                    setBookingDetails(mapped);
                    try { sessionStorage.setItem("rb_ticket_payload", JSON.stringify({ bookingDetails: mapped })); } catch {}
                    return;
                }

                if (state?.bookingDetails) {
                    if (!mounted) return;
                    setBookingDetails(state.bookingDetails);
                    try { sessionStorage.setItem("rb_ticket_payload", JSON.stringify({ bookingDetails: state.bookingDetails })); } catch {}
                    return;
                }

                try {
                    const raw = sessionStorage.getItem("rb_ticket_payload");
                    if (raw) {
                        const parsed = JSON.parse(raw);
                        if (parsed?.bookingDetails) {
                            if (!mounted) return;
                            setBookingDetails(parsed.bookingDetails);
                            return;
                        }
                    }
                } catch {}

                if (!mounted) return;
                setLoadError("No ticket data found.");
            } catch (err) {
                if (!mounted) return;
                setLoadError(err?.response?.data?.message || err?.message || "Unable to load ticket.");
            } finally {
                if (mounted) setIsLoading(false);
            }
        };

        load();
        return () => { mounted = false; };
    }, [orderId, state]);

    useEffect(() => {
        if (isLoading || !bookingDetails) return;

        const guardState = {
            rbBackGuard: true,
            page: "download-ticket",
            bookingNo: orderId || bookingDetails?.bookingNo || bookingDetails?.bookingId || "",
        };

        window.history.pushState(guardState, "", window.location.href);

        const handlePopState = () => {
            window.history.pushState(guardState, "", window.location.href);
            navigate("/my-bookings", { replace: true });
        };

        const handlePageShow = (event) => {
            if (event.persisted) {
                const hasTicket = !!(orderId || bookingDetails?.bookingNo || bookingDetails?.bookingId);
                if (!hasTicket) {
                    navigate("/my-bookings", { replace: true });
                }
            }
        };

        window.addEventListener("popstate", handlePopState);
        window.addEventListener("pageshow", handlePageShow);

        return () => {
            window.removeEventListener("popstate", handlePopState);
            window.removeEventListener("pageshow", handlePageShow);
        };
    }, [isLoading, bookingDetails, orderId, navigate]);

    if (isLoading) return (
        <div className="max-w-xl mx-auto p-6">
            <p className="text-gray-600">Loading ticket…</p>
        </div>
    );

    if (!bookingDetails) return (
        <div className="max-w-xl mx-auto p-6">
            <h1 className="text-xl font-bold text-red-600 mb-2">Ticket unavailable</h1>
            <p className="text-gray-700">{loadError || "Open this page from My Bookings or complete payment first."}</p>
            {orderId && <p className="text-sm text-gray-500 mt-2">Reference: <b>{orderId}</b></p>}
            <div className="mt-4 flex gap-2">
                <button onClick={() => navigate("/my-bookings", { replace: true })} className="px-4 py-2 rounded bg-blue-600 text-white text-sm">My Bookings</button>
                <button onClick={() => navigate("/", { replace: true })} className="px-4 py-2 rounded bg-gray-100 text-sm">Home</button>
            </div>
        </div>
    );

    /* ── derived values ── */
    const {
        bus = {},
        passenger = {},
        passengers = [],
        selectedSeats = [],
        boardingPoint = {},
        priceDetails = {},
        departureTime = "",
        date = "",
        bookingNo: bookingNoRaw = "",
        bookingNoShort = "",
        bookingId = "",
    } = bookingDetails;

    const bookingNo = String(bookingNoRaw || bookingNoShort || orderId || bookingId || "").trim();
    const busName = bus?.name || "—";
    const routeFrom = bus?.from || boardingPoint?.point || "—";
    const routeTo = bus?.to || bookingDetails?.droppingPoint?.point || "—";
    const paxCount = passengers.length || selectedSeats.length || 0;
    const totalPrice = Number(priceDetails?.totalPrice || 0);
    const departTime = departureTime || boardingPoint?.time || "—";
    const journeyDate = date
        ? new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
        : "—";
    const seatSummary = passengers.length
        ? passengers.map((p) => p?.seat).filter(Boolean).join(", ")
        : selectedSeats.join(", ");
    const qrText = `${bookingNo}|${routeFrom}->${routeTo}|${date} ${departTime}|${seatSummary}`;

    /* ── PDF export ── */
    const downloadPDF = async () => {
        const el = artboardRef.current;
        if (!el) return;
        const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
        const img = canvas.toDataURL("image/jpeg", 0.92);
        const pdf = new jsPDF("p", "mm", "a4");
        const w = pdf.internal.pageSize.getWidth();
        pdf.addImage(img, "JPEG", 0, 0, w, (canvas.height * w) / canvas.width, undefined, "FAST");
        pdf.save(`ticket-${bookingNo || "routesbook"}.pdf`);
    };

    /* ── shared section style ── */
    const SEC = { borderBottom: "1px solid #E6E8EB", padding: "14px 24px" };
    const CELL = { background: "#FAFBFC", border: "1px solid #E6E8EB", borderRadius: 12, padding: "10px 14px" };
    const LBL = { fontSize: 10, textTransform: "uppercase", letterSpacing: "0.10em", color: "#6B7280", fontWeight: 700, marginBottom: 3 };
    const VAL = { fontSize: 14, fontWeight: 700, color: "#111316" };

    return (
        <div style={{ minHeight: "100vh", background: "#F3F4F7", padding: "20px 0", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
            <style>{`
                @media print {
                    @page { size: A4; margin: 0; }
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .no-print { display: none !important; }
                    .ticket-sheet { box-shadow: none !important; border-radius: 0 !important; }
                }
            `}</style>

            {/* Actions */}
            <div className="no-print" style={{ maxWidth: 794, margin: "0 auto 12px", padding: "0 16px", display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => window.print()} style={{ padding: "8px 16px", borderRadius: 999, background: "#0F172A", color: "#fff", fontWeight: 600, fontSize: 13, border: "none", cursor: "pointer" }}>
                    🖨 Print
                </button>
                <button onClick={downloadPDF} style={{ padding: "8px 16px", borderRadius: 999, background: "#C5162E", color: "#fff", fontWeight: 600, fontSize: 13, border: "none", cursor: "pointer" }}>
                    📄 Download PDF
                </button>
                <button onClick={() => navigate("/my-bookings", { replace: true })} style={{ padding: "8px 16px", borderRadius: 999, background: "#fff", border: "1px solid #D1D5DB", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                    📚 My Bookings
                </button>
            </div>

            {/* ── Ticket Sheet ── */}
            <div
                className="ticket-sheet"
                ref={artboardRef}
                style={{
                    width: 794,
                    margin: "0 auto",
                    background: "#fff",
                    boxShadow: "0 10px 35px rgba(10,10,10,0.10)",
                    borderRadius: 16,
                    border: "1px solid #E6E8EB",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <div style={{ ...SEC, background: "#FAFBFC", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                        {logoOk
                            ? <img src={LOGO} alt="Routesbook" style={{ height: 28 }} />
                            : <span style={{ fontSize: 20, fontWeight: 800, color: "#C5162E" }}>Routesbook</span>
                        }
                        <span style={{ fontSize: 20, fontWeight: 800, color: "#111316", borderLeft: "1px solid #E6E8EB", paddingLeft: 16 }}>eTICKET</span>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: "#ECFDF3", color: "#067647", border: "1px solid #B7E6C6" }}>✅ Confirmed</span>
                    </div>
                    <div style={{ textAlign: "right", fontSize: 12, color: "#6B7280" }}>
                        <div>Booking Ref</div>
                        <div style={{ fontWeight: 700, color: "#111316", fontSize: 13 }}>{bookingNo || "—"}</div>
                    </div>
                </div>

                {/* Route */}
                <div style={{ ...SEC, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                        <div style={LBL}>Journey Route</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: "#111316" }}>
                            {routeFrom} <span style={{ color: "#C5162E" }}>→</span> {routeTo}
                        </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div style={LBL}>Travel Date</div>
                        <div style={{ ...VAL, fontSize: 15 }}>{journeyDate}</div>
                    </div>
                </div>

                {/* Schedule grid */}
                <div style={{ ...SEC, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                    {[
                        ["Departure", departTime],
                        ["Passengers", paxCount],
                        ["Bus", busName],
                        ["Board at", boardingPoint.point || "—"],
                    ].map(([label, val]) => (
                        <div key={label} style={CELL}>
                            <div style={LBL}>{label}</div>
                            <div style={VAL}>{val || "—"}</div>
                        </div>
                    ))}
                </div>

                {/* Passengers + Fare */}
                <div style={{ ...SEC, display: "flex", gap: 20, alignItems: "flex-start" }}>
                    {/* Table */}
                    <div style={{ flex: 1 }}>
                        <div style={{ ...LBL, marginBottom: 8 }}>Passenger Details</div>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: "#FAFBFC" }}>
                                    {["Name", "Seat", "Gender", "Age"].map((h) => (
                                        <th key={h} style={{ textAlign: "left", padding: "7px 10px", fontSize: 11, color: "#6B7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid #E6E8EB" }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {passengers.length ? passengers.map((p, i) => (
                                    <tr key={i} style={{ borderBottom: "1px solid #F3F4F7" }}>
                                        <td style={{ padding: "8px 10px", fontWeight: 600 }}>{p.name || "Passenger"}</td>
                                        <td style={{ padding: "8px 10px" }}>
                                            <span style={{ background: "#FFF2F4", color: "#C5162E", border: "1px solid #F3C7CF", borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{p.seat ?? "—"}</span>
                                        </td>
                                        <td style={{ padding: "8px 10px", color: "#6B7280" }}>{p.gender || "—"}</td>
                                        <td style={{ padding: "8px 10px", color: "#6B7280" }}>{p.age || "—"}</td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={4} style={{ padding: "10px", color: "#9CA3AF", fontSize: 12 }}>No passenger details provided.</td></tr>
                                )}
                            </tbody>
                        </table>
                        <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 10 }}>mTicket accepted — no printed copy required.</p>
                    </div>

                    {/* Fare */}
                    <div style={{ width: 200, background: "#FFF7F8", border: "1px solid #F3D3D8", borderRadius: 14, padding: 16, flexShrink: 0 }}>
                        <div style={LBL}>Fare Summary</div>
                        <div style={{ fontSize: 12, color: "#6B7280", margin: "8px 0 4px" }}>Seats: <b style={{ color: "#111316" }}>{seatSummary || "—"}</b></div>
                        <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 10 }}>Passengers: <b style={{ color: "#111316" }}>{paxCount}</b></div>
                        <div style={{ borderTop: "1px dashed #E5C5CA", paddingTop: 10 }}>
                            <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>Total Fare</div>
                            <div style={{ fontSize: 28, fontWeight: 800, color: "#C5162E", lineHeight: 1 }}>
                                Rs. {isFinite(totalPrice) ? totalPrice.toFixed(2) : "0.00"}
                            </div>
                            <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 6, fontStyle: "italic" }}>Incl. service charges if any</div>
                            <span style={{ marginTop: 10, display: "inline-block", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: "#ECFDF3", color: "#067647", border: "1px solid #B7E6C6" }}>✅ Payment Successful</span>
                        </div>
                    </div>
                </div>

                {/* QR + Terms */}
                <div style={{ ...SEC, borderBottom: "none", display: "flex", gap: 24, alignItems: "flex-start" }}>
                    {/* QR */}
                    <div style={{ flexShrink: 0 }}>
                        <div style={{ ...LBL, marginBottom: 8 }}>Scan / Verify</div>
                        <div style={{ border: "1px solid #E6E8EB", borderRadius: 12, padding: 10, background: "#fff", display: "inline-block" }}>
                            <QRCodeCanvas value={qrText || "routesbook"} size={100} includeMargin={false} />
                        </div>
                        <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 6, maxWidth: 130 }}>Show to boarding staff to verify this ticket.</div>
                    </div>

                    {/* Terms */}
                    <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        {[
                            {
                                title: "Routesbook responsibilities",
                                items: [
                                    "✅ Issue a valid ticket accepted by the bus operator.",
                                    "✅ Provide refund or support per applicable policy.",
                                    "✅ Maintain ticket verification via QR and booking number.",
                                ],
                            },
                            {
                                title: "Passenger guidelines",
                                items: [
                                    "🪪 Carry valid photo ID with this e-ticket.",
                                    "⏰ Arrive 15–20 min before departure.",
                                    "🚫 QR misuse may result in denied boarding.",
                                ],
                            },
                        ].map(({ title, items }) => (
                            <div key={title} style={{ background: "#FAFBFC", border: "1px solid #E6E8EB", borderRadius: 10, padding: "12px 14px" }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 8 }}>{title}</div>
                                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
                                    {items.map((it) => <li key={it} style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.5 }}>{it}</li>)}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div style={{ background: "#F9FAFB", borderTop: "1px solid #E6E8EB", padding: "10px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 11, color: "#9CA3AF" }}>routesbook.lk — Your trusted bus booking partner</div>
                    <div style={{ fontSize: 11, color: "#9CA3AF" }}>Ref: <span style={{ color: "#374151", fontWeight: 600 }}>{bookingNo || "—"}</span></div>
                </div>
            </div>
        </div>
    );
};

export default DownloadTicket;

